import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { overlays } from '@ecosystem/newron-design-system';

export type ToastMessageContextProps = {
  showToastMessage: ({ message, type, timeout }: { message: string; type?: ToastType; timeout?: number }) => void;
  hideToastMessage: () => void;
};

export type ToastType = 'success' | 'error' | 'warn' | 'info';

export enum InlineAlertType {
  Success = 'success',
  DataError = 'error',
  Info = 'info',
}

export const ToastMessageContext = createContext<ToastMessageContextProps | undefined>(undefined);

const defaultType: ToastType = 'success';

export const ToastMessageContextProvider = ({ children }: { children: React.ReactNode }) => {
  const [type, setType] = useState<ToastType>(defaultType);
  const [message, setMessage] = useState<string | undefined>('');

  const showToastMessage: ToastMessageContextProps['showToastMessage'] = useCallback(
    ({ message: toastMessage, type: toastType = 'success' }) => {
      setMessage(toastMessage);
      setType(toastType);
    },
    [],
  );

  const hideToastMessage = useCallback(() => setMessage(undefined), []);

  const value = useMemo(
    () => ({
      showToastMessage,
      hideToastMessage,
    }),
    [showToastMessage, hideToastMessage],
  );

  const { Toast } = overlays;
  return (
    <ToastMessageContext.Provider value={value}>
      {children}
      {message && (
        <Toast type={type} onDismiss={hideToastMessage} onComplete={hideToastMessage}>
          {message}
        </Toast>
      )}
    </ToastMessageContext.Provider>
  );
};

export const useToastMessage = () => {
  const context = useContext(ToastMessageContext);
  if (context === undefined) {
    throw new Error('toastMessage must be used within a ToastMessageContextProvider');
  }
  return context;
};
